import React from 'react';
import { Box, Container, Divider, Link, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BASEMAP_ATTRIBUTIONS, DATA_SOURCE_ATTRIBUTIONS, SOFTWARE_LICENCES } from '../components/credits/attributions';

const CreditsPage: React.FC = () => {
  const { t } = useTranslation('credits');

  return (
    <Container maxWidth="md" sx={{ py: 4 }} data-testid="credits.page">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('page.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('page.intro')}
        </Typography>
      </Box>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {t('dataSources.title')}
        </Typography>
        {DATA_SOURCE_ATTRIBUTIONS.map((source, index) => (
          <Box key={source.name} data-testid="credits.data-source">
            {index > 0 && <Divider sx={{ my: 2 }} />}
            <Typography variant="subtitle1" component="h3" fontWeight={600}>
              <Link href={source.sourceUrl} target="_blank" rel="noopener noreferrer">
                {source.name}
              </Link>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {source.usage}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {source.attribution}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('licenceLabel')}{' '}
              {source.licenceUrl ? (
                <Link href={source.licenceUrl} target="_blank" rel="noopener noreferrer">
                  {source.licence}
                </Link>
              ) : (
                source.licence
              )}
            </Typography>
          </Box>
        ))}
      </Paper>

      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {t('basemaps.title')}
        </Typography>
        {BASEMAP_ATTRIBUTIONS.map((basemap, index) => (
          <Box key={basemap.key} data-testid="credits.basemap">
            {index > 0 && <Divider sx={{ my: 2 }} />}
            <Typography variant="subtitle1" component="h3" fontWeight={600}>
              <Link href={basemap.sourceUrl} target="_blank" rel="noopener noreferrer">
                {basemap.name}
              </Link>
            </Typography>
            <Typography variant="body2">{basemap.attribution}</Typography>
          </Box>
        ))}
      </Paper>

      <Paper elevation={1} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {t('software.title')}
        </Typography>
        {SOFTWARE_LICENCES.map((software, index) => (
          <Box key={software.name} data-testid="credits.software">
            {index > 0 && <Divider sx={{ my: 2 }} />}
            <Typography variant="subtitle1" component="h3" fontWeight={600}>
              {software.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {software.usage}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('licenceLabel')} {software.licence}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <Link href={software.noticeUrl} target="_blank" rel="noopener noreferrer">
                {t('software.viewNotice')}
              </Link>
            </Typography>
          </Box>
        ))}
      </Paper>
    </Container>
  );
};

export default CreditsPage;
